// src/components/EligibilityCheck.js
import React, { useState, useEffect } from 'react';
import {
  Box,
 
  Button,
  Typography,
  Paper,
  LinearProgress,
  Grid,
  
  IconButton,
  Card,
  CardActionArea,
  CardContent,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import PeopleIcon from '@mui/icons-material/People';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import ContactModal from './ContactModal';
import BreakdownModal from './BreakdownModal';
import { calculateCostBreakdown } from '../utils/costCalculator';

const EligibilityCheck = () => {
  // Updated formData: removed hasSpouse and added familyType (spouse-only / spouse-child)
  const [formData, setFormData] = useState({
    monthly_salary: '',
    familyType: '', // Empty string initially
    childrenCount: 0,
    location: '',
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [canProceed, setCanProceed] = useState(false);
  const [includeAttestation, setIncludeAttestation] = useState(true);
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [dependentsData, setDependentsData] = useState([]);

  // Contact details state
  const [contactData, setContactData] = useState({ name: '', mobile: '' });
  const [contactErrors, setContactErrors] = useState({});
  const [openContactModal, setOpenContactModal] = useState(false);
  const [openBreakdownModal, setOpenBreakdownModal] = useState(false);
  const [offerCountdown, setOfferCountdown] = useState(60);

  // Updated questions array with modified first step and family options
  const questions = [
    {
      id: 'monthly_salary',
      label: "✈️ നിങ്ങളുടെ പ്രതിമാസ വരുമാനം AED-ൽ എത്രയാണ്? ",
      type: 'salary_range',  // Changed type to handle salary range selection
      helperText: 'This helps us plan your perfect UAE journey',
    },
    {
      id: 'family_details',
      label: '👨‍👩‍👧‍👦 ആരൊക്കെയാണ് സ്പോൺസർ ചെയാൻ ആഗ്രഹിക്കുന്നത്',
      type: 'family_details',
      helperText:
        'Choose between Spouse Only, Spouse + Child, or Child Only. For children options, indicate the number of children joining you.',
    },
    {
      id: 'location',
      label: '🗺️ അവർ ഇപ്പോൾ എവിടെയാണ് ഉള്ളത്?',
      type: 'select',
      helperText: 'Select one option for your starting location',
      options: [
        { value: 'uae', label: 'Already in UAE 🇦🇪' },
        { value: 'outside', label: 'Starting from Abroad 🌍' },
      ],
      validation: (value) => value !== '',
    },
  ];

  // Modal style (unchanged)
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 600,
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: { xs: 2, sm: 4 },
    borderRadius: 2,
    maxHeight: '90vh',
    overflowY: 'auto',
    mx: 'auto',
  };

  

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep((prev) => prev + 1);
      setProgress((currentStep + 1) * (100 / questions.length));
      if (questions[currentStep + 1].validation) {
        const nextVal = formData[questions[currentStep + 1].id];
        setCanProceed(questions[currentStep + 1].validation(nextVal));
      } else {
        setCanProceed(true);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
      setProgress(currentStep * (100 / questions.length));
      setCanProceed(true);
    }
  };

  // When submitting, build the dependents list based on the new family details.
  const handleSubmit = (e) => {
    e.preventDefault();

    const dependents = [];
    if (formData.familyType === 'spouse-only' || formData.familyType === 'spouse-child') {
      dependents.push({ relation: 'spouse', location: formData.location });
    }
    
    if (formData.familyType === 'spouse-child' || formData.familyType === 'child-only') {
      const childrenCount = formData.childrenCount || 1;
      for (let i = 0; i < parseInt(childrenCount, 10); i++) {
        dependents.push({ relation: 'child', location: formData.location });
      }
    }

    setDependentsData(dependents);
    const membersBreakdown = calculateCostBreakdown(dependents, includeAttestation);
    setCostBreakdown(membersBreakdown);
    setOpenContactModal(true);
  };

  const handleContactSubmit = async () => {
    let errors = {};
    if (!contactData.name.trim()) {
      errors.name = 'Name is required';
    }
    const mobileRegex = /^[0-9]{10,}$/;
    if (!mobileRegex.test(contactData.mobile)) {
      errors.mobile = 'Invalid mobile number. It should contain at least 10 digits.';
    }
    if (Object.keys(errors).length > 0) {
      setContactErrors(errors);
      return;
    }

    try {
      // Send data to Google Sheets
      const response = await fetch(
        'https://script.google.com/macros/s/AKfycbxYiyaPm_OiBMm--3i9dz6U_DXUnX-UDH3kGRMEzZzZwLfAvwV5spSihsBg5mv2k4vBYw/exec',
        {
          method: 'POST',
          mode: 'no-cors',
          body: JSON.stringify({
            name: contactData.name,
            mobile: contactData.mobile
          }),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Since we're using no-cors, we won't be able to read the response
      // Just check if the request didn't throw an error
      if (response) {
        // Close contact modal and open breakdown modal
        setOpenContactModal(false);
        setOpenBreakdownModal(true);
      }
    } catch (error) {
      console.error('Error submitting contact data:', error);
      // Optionally show an error message to the user
    }
  };

  const handleAttestationToggle = () => {
    const newIncludeAttestation = !includeAttestation;
    setIncludeAttestation(newIncludeAttestation);
    if (dependentsData.length > 0) {
      const newBreakdown = calculateCostBreakdown(dependentsData, newIncludeAttestation);
      setCostBreakdown(newBreakdown);
    }
  };

  // Countdown effect for the breakdown modal
  useEffect(() => {
    if (openBreakdownModal) {
      setOfferCountdown(60);
      const interval = setInterval(() => {
        setOfferCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setOpenBreakdownModal(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [openBreakdownModal]);

  const updateContactField = (field, value) => {
    setContactData((prev) => ({ ...prev, [field]: value }));
    setContactErrors((prev) => ({ ...prev, [field]: '' }));
  };

  // Render the current question based on the currentStep.
  const renderQuestion = () => {
    const question = questions[currentStep];
    switch (question.type) {
      case 'salary_range':
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {question.label}
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={6}>
                <Card
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      monthly_salary: '3999',
                    }));
                    setCanProceed(false);
                  }}
                  sx={{
                    border:
                      formData.monthly_salary && parseFloat(formData.monthly_salary) < 4000
                        ? '2px solid #ff4444'
                        : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                    minHeight: { xs: 120, sm: 200 },
                    '& .MuiCardContent-root': {
                      p: { xs: 1.5, sm: 2 },
                    }
                  }}
                >
                  <CardActionArea>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">Below AED 4,000</Typography>
                      {formData.monthly_salary && parseFloat(formData.monthly_salary) < 4000 && (
                        <Typography 
                          variant="body2" 
                          color="error" 
                          sx={{ 
                            mt: 1,
                            animation: 'fadeIn 0.5s ease-in',
                            '@keyframes fadeIn': {
                              '0%': { opacity: 0 },
                              '100%': { opacity: 1 },
                            },
                          }}
                        >
                          ⚠️ Sorry! A minimum salary of AED 4,000 is required for family visa sponsorship.
                          Consider exploring other visa options.
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      monthly_salary: '4000',
                    }));
                    setCanProceed(true);
                  }}
                  sx={{
                    border:
                      formData.monthly_salary && parseFloat(formData.monthly_salary) >= 4000
                        ? '2px solid #4CAF50'
                        : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                    minHeight: { xs: 120, sm: 200 },
                    '& .MuiCardContent-root': {
                      p: { xs: 1.5, sm: 2 },
                    }
                  }}
                >
                  <CardActionArea>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6">AED 4,000 or Above</Typography>
                      {formData.monthly_salary && parseFloat(formData.monthly_salary) >= 4000 && (
                        <Typography 
                          variant="body2" 
                          color="success.main"
                          sx={{ 
                            mt: 1,
                            animation: 'fadeIn 0.5s ease-in',
                            '@keyframes fadeIn': {
                              '0%': { opacity: 0 },
                              '100%': { opacity: 1 },
                            },
                          }}
                        >
                          🎉 Great news! You meet the minimum salary requirement. 
                          Let's continue planning your family's journey to UAE!
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  fontStyle: 'italic',
                  animation: formData.monthly_salary ? 'fadeIn 0.5s ease-in' : 'none',
                }}
              >
                {!formData.monthly_salary 
                  ? "👆 Select your salary range to check eligibility"
                  : parseFloat(formData.monthly_salary) >= 4000 
                    ? "✨ Click 'Next' to proceed with your journey"
                    : "💡 Need assistance? Contact our visa experts for alternative solutions"}
              </Typography>
            </Box>
          </Box>
        );
      case 'family_details':
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {question.label}
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              <Grid item xs={12} sm={4}>
                <Card
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      familyType: 'spouse-only',
                      childrenCount: 0,
                    }));
                    setCanProceed(true);
                  }}
                  sx={{
                    border: formData.familyType === 'spouse-only' ? '2px solid #2196F3' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                    height: '100%',
                    position: 'relative',
                    minHeight: { xs: 150, sm: 200 },
                    '& .MuiCardContent-root': {
                      p: { xs: 1.5, sm: 2 },
                    }
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2
                  }}>
                    <Box sx={{ 
                      width: { xs: 50, sm: 70 },
                      height: { xs: 50, sm: 70 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <PeopleIcon sx={{ 
                        fontSize: { xs: 25, sm: 35 },
                        color: 'white' 
                      }} />
                    </Box>
                    <Typography variant="h6">Spouse Only</Typography>
                    {formData.familyType === 'spouse-only' && (
                      <Typography 
                        variant="body2" 
                        color="primary"
                        sx={{ 
                          animation: 'fadeIn 0.5s ease-in',
                          '@keyframes fadeIn': {
                            '0%': { opacity: 0 },
                            '100%': { opacity: 1 },
                          },
                        }}
                      >
                        ✨ Perfect for couples
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      familyType: 'spouse-child',
                      childrenCount: prev.childrenCount || 1,
                    }));
                    setCanProceed(true);
                  }}
                  sx={{
                    border: formData.familyType === 'spouse-child' ? '2px solid #2196F3' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                    height: '100%',
                    position: 'relative',
                    minHeight: { xs: 150, sm: 200 },
                    '& .MuiCardContent-root': {
                      p: { xs: 1.5, sm: 2 },
                    }
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2
                  }}>
                    <Box sx={{ 
                      width: { xs: 50, sm: 70 },
                      height: { xs: 50, sm: 70 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PeopleIcon sx={{ 
                          fontSize: { xs: 25, sm: 35 },
                          color: 'white' 
                        }} />
                        <ChildFriendlyIcon sx={{ 
                          fontSize: { xs: 25, sm: 35 },
                          color: 'white', ml: 0.5 
                        }} />
                      </Box>
                    </Box>
                    <Typography variant="h6">Spouse + Children</Typography>
                    {formData.familyType === 'spouse-child' && (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                          animation: 'fadeIn 0.5s ease-in',
                          zIndex: 2
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent card click when using counter
                      >
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              childrenCount: Math.max(1, (prev.childrenCount || 1) - 1)
                            }));
                          }}
                        >
                          <Remove />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center' }}>
                          {formData.childrenCount || 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              childrenCount: (prev.childrenCount || 1) + 1
                            }));
                          }}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      familyType: 'child-only',
                      childrenCount: prev.childrenCount || 1,
                    }));
                    setCanProceed(true);
                  }}
                  sx={{
                    border: formData.familyType === 'child-only' ? '2px solid #2196F3' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                    height: '100%',
                    position: 'relative',
                    minHeight: { xs: 150, sm: 200 },
                    '& .MuiCardContent-root': {
                      p: { xs: 1.5, sm: 2 },
                    }
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    minHeight: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2
                  }}>
                    <Box sx={{ 
                      width: { xs: 50, sm: 70 },
                      height: { xs: 50, sm: 70 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <ChildFriendlyIcon sx={{ 
                        fontSize: { xs: 25, sm: 35 },
                        color: 'white' 
                      }} />
                    </Box>
                    <Typography variant="h6">Children Only</Typography>
                    {formData.familyType === 'child-only' && (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                          animation: 'fadeIn 0.5s ease-in',
                          zIndex: 2
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent card click when using counter
                      >
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              childrenCount: Math.max(1, (prev.childrenCount || 1) - 1)
                            }));
                          }}
                        >
                          <Remove />
                        </IconButton>
                        <Typography variant="h6" sx={{ minWidth: 30, textAlign: 'center' }}>
                          {formData.childrenCount || 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              childrenCount: (prev.childrenCount || 1) + 1
                            }));
                          }}
                        >
                          <Add />
                        </IconButton>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            {!formData.familyType && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 3, 
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                👆 Please select one of the options above to continue
              </Typography>
            )}
          </Box>
        );
      case 'select':
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {question.label}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              {question.helperText}
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center">
              <Grid item xs={12} sm={6}>
                <Card
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, location: 'uae' }));
                    setCanProceed(true);
                  }}
                  sx={{
                    border: formData.location === 'uae' ? '2px solid #2196F3' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                    height: '100%',
                    '& .MuiCardContent-root': {
                      p: { xs: 2, sm: 3 },
                    }
                  }}
                >
                  <CardActionArea sx={{ height: '100%' }}>
                    <CardContent sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                    }}>
                      <Box sx={{ 
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #00732f 0%, #ffffff 50%, #000000 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1
                      }}>
                        <img 
                          src="https://flagcdn.com/ae.svg" 
                          alt="UAE Flag" 
                          style={{ 
                            width: '40px', 
                            height: '40px',
                            objectFit: 'contain' 
                          }} 
                        />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Already in UAE
                      </Typography>
                      {formData.location === 'uae' && (
                        <Typography 
                          variant="body2" 
                          color="primary"
                          sx={{ 
                            mt: 1,
                            animation: 'fadeIn 0.5s ease-in',
                            '@keyframes fadeIn': {
                              '0%': { opacity: 0 },
                              '100%': { opacity: 1 },
                            },
                          }}
                        >
                          ✨ Perfect! We'll expedite your family's process
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, location: 'outside' }));
                    setCanProceed(true);
                  }}
                  sx={{
                    border: formData.location === 'outside' ? '2px solid #2196F3' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3,
                    },
                    height: '100%',
                    '& .MuiCardContent-root': {
                      p: { xs: 2, sm: 3 },
                    }
                  }}
                >
                  <CardActionArea sx={{ height: '100%' }}>
                    <CardContent sx={{ 
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                    }}>
                      <Box sx={{ 
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1
                      }}>
                        <Typography variant="h4">🌍</Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Starting from Abroad
                      </Typography>
                      {formData.location === 'outside' && (
                        <Typography 
                          variant="body2" 
                          color="primary"
                          sx={{ 
                            mt: 1,
                            animation: 'fadeIn 0.5s ease-in',
                            '@keyframes fadeIn': {
                              '0%': { opacity: 0 },
                              '100%': { opacity: 1 },
                            },
                          }}
                        >
                          🌟 We'll guide you through the entire journey
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>
            {!formData.location && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 3, 
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                👆 Select your starting location to proceed
              </Typography>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  // Update the useEffect or wherever you handle step changes
  useEffect(() => {
    if (currentStep === 1) { // Family details step
      setCanProceed(formData.familyType !== ''); // Only allow proceeding if a family type is selected
    }
  }, [currentStep, formData.familyType]);

  return (
    <>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, sm: 3 },
          maxWidth: 600,
          mx: 'auto',
          mt: 4,
          mb: 4,
          background: 'linear-gradient(to right bottom, #ffffff, #f8f9fa)',
          borderRadius: 2,
          width: '95%',
        }}
      >
        <Typography
          variant="h5"
          sx={{ 
            mb: 2, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontSize: { xs: '1.25rem', sm: '1.5rem' }
          }}
        >
          <img 
            src="https://flagcdn.com/ae.svg" 
            alt="UAE Flag" 
            style={{ 
              width: '24px', 
              height: '24px',
              objectFit: 'contain' 
            }} 
          />
          Plan Your UAE Family Adventure
        </Typography>
        <Box sx={{ width: '100%', mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 10,
              borderRadius: 5,
              '& .MuiLinearProgress-bar': {
                borderRadius: 5,
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              },
            }}
          />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Journey Progress: Step {currentStep + 1} of {questions.length}
          </Typography>
        </Box>
        <Box
          component="form"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canProceed) {
              e.preventDefault();
              handleNext();
            }
          }}
        >
          {renderQuestion()}
          <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mt: { xs: 2, sm: 3 } }}>
            {currentStep !== 0 && (
              <Grid item xs={12} sm={6}>
                <Button
                  onClick={handlePrevious}
                  variant="outlined"
                  fullWidth
                >
                  Previous
                </Button>
              </Grid>
            )}
            <Grid item xs={12} sm={currentStep === 0 ? 12 : 6}>
              {currentStep === 0 && parseFloat(formData.monthly_salary) < 4000 ? (
                <Button
                  variant="contained"
                  fullWidth
                  href="https://wa.me/971525200725" // Replace with your actual WhatsApp number
                  target="_blank"
                  sx={{
                    background: '#25D366', // WhatsApp green color
                    '&:hover': {
                      background: '#128C7E', // Darker WhatsApp green
                    },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="white"
                  >
                    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.419-.1.824z"/>
                  </svg>
                  Chat with Visa Expert
                </Button>
              ) : currentStep === questions.length - 1 ? (
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={!canProceed}
                  fullWidth
                  sx={{
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  }}
                >
                  Check Journey Eligibility
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  variant="contained"
                  disabled={!canProceed}
                  fullWidth
                  sx={{
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  }}
                >
                  Next
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Render the Contact Modal */}
      <ContactModal
        open={openContactModal}
        onClose={() => setOpenContactModal(false)}
        contactData={contactData}
        onChange={updateContactField}
        contactErrors={contactErrors}
        onSubmit={handleContactSubmit}
        modalStyle={modalStyle}
      />

      {/* Render the Breakdown Modal */}
      <BreakdownModal
        open={openBreakdownModal}
        onClose={() => setOpenBreakdownModal(false)}
        costBreakdown={costBreakdown}
        dependentsData={dependentsData}
        includeAttestation={includeAttestation}
        onAttestationToggle={handleAttestationToggle}
        offerCountdown={offerCountdown}
        modalStyle={modalStyle}
      />
    </>
  );
};

export default EligibilityCheck;
